/* eslint-disable no-magic-numbers */

import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';

import { UITree, UITreeNode, getFilteredTree } from '@futrli/ui-components';
import { ChartOfAccountBulkFlatItem } from '@futrli-sdk/consolidations';
import { useDropzone } from '@futrli/shared-services';

import { AvailableAccount } from './available-account';
import { COACategory } from '../../../consolidation-context/types';
import { useConsolidationContext } from '../../../consolidation-context/consolidation-context';
import styles from './available-account-list.scss';

export interface AvailableAccountsListProps {
  className?: string;
  allowDrop?: boolean;
  isDragging?: boolean;
  onDragStart?: (event: React.DragEvent, id: string) => void;
  onDrop?: (event: React.DragEvent) => void;
  searchCriteria?: string;
}

export const AvailableAccountsList = ({
  className,
  allowDrop = false,
  isDragging,
  onDragStart,
  onDrop,
  searchCriteria = ''
}: AvailableAccountsListProps) => {
  const {
    eliminations: {
      coa: { tree },
      eliminationGroups
    }
  } = useConsolidationContext();

  const { handleDragLeave, handleDragOver, targetted } = useDropzone(allowDrop, !!isDragging);

  const noGroupsAvailable = !Object.keys(eliminationGroups).length;

  const filteredOptions = useMemo(() => {
    return tree ? getFilteredTree(tree, searchCriteria) : undefined;
  }, [tree, searchCriteria]);

  const AvailableAccountWithDrag = useCallback(
    (data: UITreeNode<COACategory | ChartOfAccountBulkFlatItem>) => (
      <AvailableAccount {...data} disabled={noGroupsAvailable} onDragStart={onDragStart} />
    ),
    [noGroupsAvailable, onDragStart]
  );

  if (!filteredOptions) {
    return null;
  }

  return (
    <div
      className={clsx(className, styles.container, {
        [styles.targetable]: isDragging && allowDrop,
        [styles.targetted]: targetted
      })}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={onDrop}
    >
      <UITree<COACategory | ChartOfAccountBulkFlatItem>
        tree={filteredOptions}
        component={AvailableAccountWithDrag}
        searchCriteria={searchCriteria}
        className={styles.tree}
      />
    </div>
  );
};
